/**
 * Resolve term statuses for an assessment using the same logic as DataCollectionStatus.
 * Returns array of { name, ended, isCurrent }
 */
export const resolveTermStatuses = (assessment, insightsTerms, canvasTerms) => {
  const terms = assessment.terms || [];
  const termStatuses = [];

  terms.forEach((term) => {
    let resolvedEndAt = term.end_at || null;
    if (!resolvedEndAt && term.insights_term_id && insightsTerms) {
      const it = insightsTerms.find((i) => i.id === term.insights_term_id);
      if (it) resolvedEndAt = it.end_at;
    }

    const termCanvasTerms = term.canvas_terms || [];
    if (termCanvasTerms.length > 0) {
      termCanvasTerms.forEach((ct) => {
        const ctEndAt = ct.end_at || resolvedEndAt;
        const ended = ctEndAt ? new Date(ctEndAt) < new Date() : false;
        termStatuses.push({
          name: ct.name || term.name || "",
          ended,
          endAt: ctEndAt,
        });
      });
    } else {
      const ended = resolvedEndAt
        ? new Date(resolvedEndAt) < new Date()
        : false;
      termStatuses.push({ name: term.name || "", ended, endAt: resolvedEndAt });
    }
  });

  // For external assessments, resolve assessing_terms if no term statuses yet
  if (
    assessment.assessment_type === "external" &&
    assessment.assessing_terms &&
    assessment.assessing_terms.length > 0 &&
    termStatuses.length === 0
  ) {
    assessment.assessing_terms.forEach((termId) => {
      if (canvasTerms && canvasTerms.length > 0) {
        const ct = canvasTerms.find((c) => c.id === termId);
        if (ct) {
          const insightsTerm = insightsTerms
            ? insightsTerms.find((it) => it.id === ct.insights_term_id)
            : null;
          const name = insightsTerm ? insightsTerm.name : ct.name || termId;
          const endAt = insightsTerm ? insightsTerm.end_at : ct.end_at;
          const ended = endAt ? new Date(endAt) < new Date() : false;
          termStatuses.push({ name, ended, endAt });
          return;
        }
      }
      if (insightsTerms && insightsTerms.length > 0) {
        for (const it of insightsTerms) {
          const found = (it.canvas_terms || []).find(
            (ct) => ct.id === termId || ct.ddl_id === termId
          );
          if (found) {
            const ended = it.end_at ? new Date(it.end_at) < new Date() : false;
            termStatuses.push({
              name: it.name || termId,
              ended,
              endAt: it.end_at,
            });
            return;
          }
        }
      }
    });
  }

  // Determine which term is "current" (first non-ended term)
  let currentFound = false;
  return termStatuses.map((ts) => {
    if (!ts.ended && !currentFound) {
      currentFound = true;
      return { ...ts, isCurrent: true };
    }
    return { ...ts, isCurrent: false };
  });
};

/**
 * Build lookup maps from plan.sections for hydrating agenda items at render time.
 * Returns { assessmentMap, qualitativeMeasureMap }
 */
export const buildPlanLookups = (plan) => {
  const assessmentMap = {};
  const qualitativeMeasureMap = {};

  if (!plan?.sections) return { assessmentMap, qualitativeMeasureMap };

  plan.sections.forEach((section) => {
    const sectionName = section.name || "";

    if (section.assessments) {
      section.assessments.forEach((assessment) => {
        const key = assessment.id;
        if (key) {
          assessmentMap[key] = { ...assessment, sectionName };
        }
      });
    }

    if (section.qualitative_measures) {
      section.qualitative_measures.forEach((measure) => {
        const key = measure.ddl_id;
        if (key) {
          qualitativeMeasureMap[key] = { ...measure, sectionName };
        }
      });
    }
  });

  return { assessmentMap, qualitativeMeasureMap };
};
